/* eslint-disable no-nested-ternary */
import { useMemo, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, Divider, IconButton, List, ListItemButton, Tab, Tabs, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import InfiniteScroll from 'react-infinite-scroller';
import { dispatch, useSelector } from 'store';
import { fetchTabTicketsNext, toggleTicketAssignmentSection } from 'store/slices/userAssignmentTicketSectionSlice';
import { emptyTabState, TICKET_OPEN_STATUS, TICKET_SECTION_TABS } from './constants';
import { mapTicketItems } from './mapTicketItem';
import { getTicketCount, openTicketDetail } from './utils';
import PersonIcon from '@mui/icons-material/Person';
import getPriorityChipByType from 'views/pages/internal-commenting/commons';

const TicketListItem = ({ ticket, onClick }) => {
    const { agencyName, timeAgo, description, priority, category, status, statusLabel, priorityId } = ticket;
    const isOpen = status === TICKET_OPEN_STATUS.OPEN;
    const isClosed = status === TICKET_OPEN_STATUS.CLOSE;

    return (
        <ListItemButton
            alignItems="flex-start"
            onClick={onClick}
            sx={{
                flexDirection: 'column',
                gap: 0.75,
                px: 2,
                py: 1.5,
                alignItems: 'stretch',
                '&:hover': { bgcolor: 'grey.50' }
            }}
        >
            <Box display="flex" alignItems="center" width="100%" gap={0.75}>
                <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {agencyName}
                </Typography>
                {statusLabel ? (
                    <Chip
                        size="small"
                        label={statusLabel}
                        sx={{
                            height: 22,
                            flexShrink: 0,
                            fontWeight: 600,
                            bgcolor: isOpen ? 'success.light' : isClosed ? 'grey.200' : 'grey.100',
                            color: isOpen ? 'success.dark' : 'text.secondary',
                            '& .MuiChip-label': { px: 1 }
                        }}
                    />
                ) : null}
                <Typography variant="caption" color="text.secondary" flexShrink={0}>
                    {timeAgo}
                </Typography>
            </Box>

            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}
            >
                {description}
            </Typography>

            <Box display="flex" flexWrap="wrap" gap={0.75} width="100%">
                {priority ? (
                    <Chip
                        size="small"
                        label={getPriorityChipByType(priorityId, false)?.text}
                        sx={{
                            height: 22,
                            bgcolor: getPriorityChipByType(priorityId, false)?.bgColor,
                            color: getPriorityChipByType(priorityId, false)?.color,
                            fontWeight: 500,
                            '& .MuiChip-label': { px: 1 }
                        }}
                    />
                ) : null}
                {category ? (
                    <Chip
                        size="small"
                        label={category}
                        sx={{
                            height: 22,
                            bgcolor: 'grey.100',
                            color: 'text.secondary',
                            fontWeight: 500,
                            '& .MuiChip-label': { px: 1 }
                        }}
                    />
                ) : null}
            </Box>
        </ListItemButton>
    );
};

const EmptyTicketsState = ({ isMemberNotFound }) => (
    <Box
        sx={{
            flex: 1,
            minHeight: 220,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: 5,
            gap: 1.5,
            textAlign: 'center'
        }}
    >
        {isMemberNotFound ? (
            <>
                <Box
                    sx={{
                        width: 62,
                        height: 62,
                        borderRadius: '50%',
                        bgcolor: 'error.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 0.5
                    }}
                >
                    <PersonIcon sx={{ fontSize: 30, color: 'error.dark' }} />
                </Box>
                <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ fontSize: 18 }}>
                    Member not found
                </Typography>
            </>
        ) : (
            <>
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        bgcolor: 'success.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 0.5
                    }}
                >
                    <CheckIcon sx={{ fontSize: 28, color: 'success.dark' }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                    You are all caught up
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    No open tickets assigned to you.
                </Typography>
            </>
        )}
    </Box>
);

const TickerSection = () => {
    const ticketState = useSelector((state) => state.userTicketSectionReducer);
    const { isInitialLoading = false, isLoading = false, isMemberNotFound = false } = ticketState || {};
    const scrollParentRef = useRef(null);

    const [activeTab, setActiveTab] = useState(TICKET_SECTION_TABS[0]?.id);

    const activeTabConfig = useMemo(() => TICKET_SECTION_TABS.find((item) => item.id === activeTab) || TICKET_SECTION_TABS[0], [activeTab]);

    const activeTabData = ticketState?.[activeTabConfig?.dataKey] || emptyTabState();

    const totalCount = useMemo(() => getTicketCount(ticketState), [ticketState]);

    const activeTabTickets = useMemo(
        () => mapTicketItems(activeTabConfig?.id, activeTabData?.items || []),
        [activeTabConfig?.id, activeTabData?.items]
    );

    const hasMore = Boolean(activeTabData?.hasMore);

    const handleTabChange = (_event, nextTab) => {
        setActiveTab(nextTab);
    };

    const handleLoadMore = () => {
        if (isLoading || isInitialLoading) return;
        if (!activeTabData?.hasMore || !activeTabData?.next) return;
        dispatch(fetchTabTicketsNext(activeTab));
    };

    const showInitialLoader = isInitialLoading && activeTabTickets.length === 0 && !activeTabData?.isLoaded;
    const showEmpty = (!showInitialLoader && activeTabData?.isLoaded && activeTabTickets.length === 0) || isMemberNotFound;

    return (
        <Box
            sx={{
                position: 'absolute',
                bottom: 72,
                right: 0,
                width: 420,
                maxWidth: 'calc(100vw - 32px)',
                maxHeight: 'calc(100vh - 120px)',
                height: '600px',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'grey.300',
                boxShadow: 6,
                zIndex: 999999
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    bgcolor: 'secondary.main',
                    color: 'secondary.contrastText'
                }}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1" color="inherit" fontWeight={700}>
                        My Tickets
                    </Typography>
                    <Box
                        sx={{
                            minWidth: 22,
                            height: 22,
                            px: 0.75,
                            borderRadius: 10,
                            bgcolor: 'rgba(0,0,0,0.28)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Typography variant="caption" color="inherit" fontWeight={600}>
                            {totalCount}
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    size="small"
                    onClick={() => dispatch(toggleTicketAssignmentSection())}
                    aria-label="close my tickets"
                    sx={{ color: 'inherit' }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ px: 1.5, pt: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    aria-label="my tickets section tabs"
                    TabIndicatorProps={{ sx: { display: 'none' } }}
                    sx={{
                        minHeight: 36,
                        '& .MuiTabs-scrollButtons.Mui-disabled': {
                            display: 'none'
                        },
                        '& .MuiTabs-flexContainer': {
                            gap: 1,
                            border: 'none !important'
                        },
                        '& .MuiTab-root': {
                            minHeight: 32,
                            minWidth: 'auto',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 10,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: 13,
                            color: 'text.primary',
                            border: '1px solid',
                            borderColor: 'grey.300',
                            bgcolor: 'background.paper'
                        },
                        '& .Mui-selected': {
                            color: '#fff !important',
                            bgcolor: 'secondary.main',
                            borderColor: 'secondary.main'
                        }
                    }}
                >
                    {TICKET_SECTION_TABS.map((tab) => (
                        <Tab key={tab.id} value={tab.id} label={tab.label} disableRipple />
                    ))}
                </Tabs>
            </Box>

            <Box
                ref={scrollParentRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    py: 0
                }}
            >
                {showInitialLoader ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
                        <CircularProgress size={28} color="secondary" />
                    </Box>
                ) : showEmpty ? (
                    <EmptyTicketsState isMemberNotFound={isMemberNotFound} />
                ) : (
                    <InfiniteScroll
                        key={activeTab}
                        pageStart={1}
                        loadMore={handleLoadMore}
                        hasMore={hasMore}
                        useWindow={false}
                        initialLoad={false}
                        threshold={120}
                        getScrollParent={() => scrollParentRef.current}
                        loader={
                            <Box key={`ticket-scroll-loader-${activeTab}`} display="flex" justifyContent="center" py={1.5}>
                                <CircularProgress size={22} color="secondary" />
                            </Box>
                        }
                    >
                        <List disablePadding>
                            {activeTabTickets.map((ticket, index) => (
                                <Box key={ticket.id || index}>
                                    <TicketListItem ticket={ticket} onClick={() => openTicketDetail(activeTabConfig?.id, ticket)} />
                                    {index < activeTabTickets.length - 1 && <Divider />}
                                </Box>
                            ))}
                        </List>
                    </InfiniteScroll>
                )}
            </Box>
        </Box>
    );
};

export default TickerSection;
